class ReferenceLocations extends HTMLElement {
  static editorSelection = new Map();

  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const {signal} = this.controller;
    this.panels = [...this.querySelectorAll('[data-location-panel]')];
    this.entries = [...this.querySelectorAll('[data-location-entry]')];
    this.options = [...this.querySelectorAll('[data-location-option]')];
    this.picker = this.querySelector('[data-location-picker]');
    this.trigger = this.querySelector('[data-location-trigger]');
    this.optionList = this.querySelector('[data-location-options]');
    this.currentName = this.querySelector('[data-location-current]');
    if (!this.panels.length || this.options.length !== this.panels.length) return;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = matchMedia('(max-width: 767px)');
    this.mapWrapper = this.querySelector('[data-location-map-wrapper]');
    this.mapStage = this.querySelector('[data-location-map-stage]');
    this.currentFrame = this.querySelector('[data-location-map]');
    this.status = this.querySelector('[data-map-status]');
    this.message = this.querySelector('[data-map-message]');
    this.retry = this.querySelector('[data-map-retry]');
    this.picker.hidden = this.panels.length < 2;
    this.querySelector('[data-selector-label]').hidden = this.panels.length < 2;
    this.entries.forEach(entry => { entry.hidden = false; });
    this.addEventListener('click', event => {
      const entry = event.target.closest('[data-location-entry]');
      const option = event.target.closest('[data-location-option]');
      if (entry) this.select(this.entries.indexOf(entry), {instant: event.detail === 0});
      if (option) {
        this.select(this.options.indexOf(option), {instant: event.detail === 0});
        this.closePicker(true);
      }
    }, {signal});
    this.trigger.addEventListener('click', event => {
      this.toggleAttribute('data-location-instant', event.detail === 0 || this.motion.matches);
      if (this.optionList.hidden) this.openPicker();
      else this.closePicker();
    }, {signal});
    this.picker.addEventListener('keydown', event => this.pickerKeydown(event), {signal});
    document.addEventListener('pointerdown', event => {
      if (!this.picker.contains(event.target)) this.closePicker();
    }, {signal});
    this.picker.addEventListener('focusout', event => {
      // Focus is temporarily on body while moving between options.
      // Check its destination so the list stays open until the click completes.
      if (!this.picker.contains(event.relatedTarget)) this.closePicker();
    }, {signal});
    this.addEventListener('pointerdown', event => {
      if (event.target.closest('[data-location-entry], [data-location-trigger], [data-location-option]')) {
        this.toggleAttribute('data-location-instant', this.motion.matches);
      }
    }, {signal});
    this.addEventListener('keydown', () => this.setAttribute('data-location-instant', ''), {signal});
    this.mobile.addEventListener('change', () => {
      const focused = document.activeElement;
      this.closePicker();
      this.updatePanels();
      if (this.mobile.matches && this.entries.includes(focused)) this.trigger.focus({preventScroll: true});
      if (!this.mobile.matches && this.picker.contains(focused)) this.entries[this.index].focus({preventScroll: true});
    }, {signal});
    this.addEventListener('shopify:block:select', event => {
      const index = this.panels.findIndex(panel => panel.dataset.blockId === event.detail?.blockId || panel === event.target || panel.contains(event.target));
      if (index >= 0) { this.closePicker(); this.select(index, {instant: true}); }
    }, {signal});
    this.retry?.addEventListener('click', event => this.showMap(this.panels[this.index], event.detail === 0), {signal});
    this.motion.addEventListener('change', () => {
      this.toggleAttribute('data-location-instant', this.motion.matches);
    }, {signal});

    if (this.mapWrapper) {
      this.mapResize = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (width > 0) this.mapWrapper.style.setProperty('--hf-map-scale', Math.min(1, width / 401));
      });
      this.mapResize.observe(this.mapWrapper);
    }
    const remembered = window.Shopify?.designMode && ReferenceLocations.editorSelection.get(this.dataset.sectionId);
    const initial = this.panels.findIndex(panel => panel.dataset.blockId === remembered);
    this.select(Math.max(0, initial), {instant: true});
    this.setAttribute('data-locations-ready', '');
    if (window.Shopify?.designMode) {
      // Shopify can edit a block's text inline without re-rendering the section.
      this.editorNames = new MutationObserver(() => {
        this.panels.forEach((panel, index) => {
          const name = panel.querySelector('.gms-location-title').textContent.trim();
          const label = this.options[index].querySelector('[data-option-name]');
          if (label.textContent !== name) label.textContent = name;
          panel.querySelector('[data-location]').dataset.mapTitle = `Google Map: ${name}`;
          if (index === this.index) {
            this.currentName.textContent = name;
            if (this.currentFrame) this.currentFrame.title = `Google Map: ${name}`;
          }
        });
      });
      this.editorNames.observe(this.querySelector('[data-location-panels]'), {childList: true, characterData: true, subtree: true});
    }
  }

  select(index, {instant = false} = {}) {
    if (!this.panels[index]) return;
    instant ||= this.motion.matches;
    this.toggleAttribute('data-location-instant', instant);
    this.index = index;
    this.options.forEach((option, position) => {
      const active = position === index;
      option.setAttribute('aria-selected', String(active));
      this.entries[position].setAttribute('aria-pressed', String(active));
    });
    this.currentName.textContent = this.panels[index].querySelector('.gms-location-title').textContent.trim();
    this.updatePanels();
    if (window.Shopify?.designMode) ReferenceLocations.editorSelection.set(this.dataset.sectionId, this.panels[index].dataset.blockId);
    this.showMap(this.panels[index], instant);
  }

  updatePanels() {
    this.panels.forEach((panel, position) => {
      const active = position === this.index;
      const concealed = this.mobile.matches && !active;
      panel.classList.toggle('is-active', active);
      panel.inert = concealed;
      panel.setAttribute('aria-hidden', String(concealed));
    });
  }

  openPicker() {
    if (!this.mobile.matches || this.picker.hidden) return;
    this.optionList.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.focusOption(this.index);
  }

  closePicker(restoreFocus = false) {
    if (!this.optionList) return;
    this.optionList.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.options.forEach(option => { option.tabIndex = -1; });
    this.typeahead = '';
    clearTimeout(this.typeaheadTimeout);
    if (restoreFocus) this.trigger.focus({preventScroll: true});
  }

  focusOption(index) {
    const option = this.options[index];
    this.options.forEach(item => { item.tabIndex = item === option ? 0 : -1; });
    option.focus({preventScroll: true});
    const top = option.offsetTop;
    const bottom = top + option.offsetHeight;
    if (top < this.optionList.scrollTop) this.optionList.scrollTop = top;
    else if (bottom > this.optionList.scrollTop + this.optionList.clientHeight) this.optionList.scrollTop = bottom - this.optionList.clientHeight;
  }

  pickerKeydown(event) {
    if (event.key === 'Escape') {
      if (!this.optionList.hidden) { event.preventDefault(); this.closePicker(true); }
      return;
    }
    if (event.key === 'Tab') { if (!this.optionList.hidden) this.closePicker(true); return; }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      if (this.optionList.hidden) { this.openPicker(); return; }
      let index = this.options.indexOf(document.activeElement);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.options.length - 1;
      else index = (index + (event.key === 'ArrowDown' ? 1 : -1) + this.options.length) % this.options.length;
      this.focusOption(index);
      return;
    }
    if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      if (this.optionList.hidden) this.openPicker();
      this.typeahead = (this.typeahead || '') + event.key.toLocaleLowerCase();
      clearTimeout(this.typeaheadTimeout);
      this.typeaheadTimeout = setTimeout(() => { this.typeahead = ''; }, 700);
      const index = this.options.findIndex(option => option.textContent.trim().toLocaleLowerCase().startsWith(this.typeahead));
      if (index >= 0) this.focusOption(index);
    }
  }

  mapUrl(panel) {
    try {
      const url = new URL(panel.querySelector('[data-location]').dataset.mapEmbed);
      if (url.origin === 'https://www.google.com' && url.pathname === '/maps/embed' && url.searchParams.get('pb') && !url.username && !url.password) return url.href;
    } catch {}
    return '';
  }

  cancelPending() {
    clearTimeout(this.mapTimeout);
    clearTimeout(this.retireTimeout);
    this.pendingFrame?.remove();
    this.pendingFrame = null;
    this.retiredFrame?.remove();
    this.retiredFrame = null;
  }

  showMap(panel, instant = false) {
    if (!this.mapStage) return;
    this.cancelPending();
    const destination = this.mapUrl(panel);
    this.status.hidden = true;
    this.retry.hidden = true;
    if (!destination) {
      this.showMapError('unavailable');
      return;
    }
    if (this.currentFrame?.src === destination) {
      this.currentFrame.title = panel.querySelector('[data-location]').dataset.mapTitle;
      this.currentFrame.inert = false;
      this.currentFrame.removeAttribute('aria-hidden');
      this.currentFrame.removeAttribute('tabindex');
      this.currentFrame.style.opacity = '';
      this.mapStage.setAttribute('aria-busy', 'false');
      this.dataset.mapState = 'ready';
      return;
    }
    this.dataset.mapState = 'loading';
    this.mapStage.setAttribute('aria-busy', 'true');
    if (this.currentFrame) {
      this.currentFrame.inert = true;
      this.currentFrame.setAttribute('aria-hidden', 'true');
      this.currentFrame.tabIndex = -1;
    }
    const frame = document.createElement('iframe');
    frame.setAttribute('data-location-map', '');
    frame.setAttribute('data-map-pending', '');
    frame.title = panel.querySelector('[data-location]').dataset.mapTitle;
    frame.width = '1440';
    frame.height = '500';
    frame.loading = 'eager';
    frame.allowFullscreen = true;
    frame.referrerPolicy = 'no-referrer-when-downgrade';
    frame.inert = true;
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    this.pendingFrame = frame;
    const fail = () => {
      if (this.pendingFrame !== frame) return;
      this.showMapError('error');
      console.warn('[OudLibrary]', {level: 'warning', feature: 'locations', code: 'map-load-failed'});
    };
    frame.addEventListener('error', fail, {once: true, signal: this.controller.signal});
    frame.addEventListener('load', () => {
      if (!this.isConnected || this.pendingFrame !== frame) return;
      clearTimeout(this.mapTimeout);
      const previous = this.currentFrame;
      this.currentFrame = frame;
      this.pendingFrame = null;
      frame.removeAttribute('data-map-pending');
      frame.removeAttribute('aria-hidden');
      frame.removeAttribute('tabindex');
      frame.inert = false;
      this.dataset.mapState = 'ready';
      this.mapStage.setAttribute('aria-busy', 'false');
      if (previous) {
        previous.style.opacity = '0';
        this.retiredFrame = previous;
        this.retireTimeout = setTimeout(() => {
          previous.remove();
          if (this.retiredFrame === previous) this.retiredFrame = null;
        }, instant || this.motion.matches ? 0 : 200);
      }
    }, {once: true, signal: this.controller.signal});
    frame.src = destination;
    this.mapStage.append(frame);
    this.mapTimeout = setTimeout(fail, 12000);
  }

  showMapError(state) {
    this.cancelPending();
    this.currentFrame?.remove();
    this.currentFrame = null;
    this.dataset.mapState = state;
    this.mapStage.setAttribute('aria-busy', 'false');
    this.message.textContent = state === 'unavailable' ? this.dataset.mapUnavailable : this.dataset.mapError;
    this.status.hidden = false;
    this.retry.hidden = state !== 'error';
  }

  disconnectedCallback() {
    this.cancelPending();
    this.controller?.abort();
    this.mapResize?.disconnect();
    this.editorNames?.disconnect();
    clearTimeout(this.typeaheadTimeout);
    this.controller = null;
    this.removeAttribute('data-locations-ready');
  }
}

if (!customElements.get('ol-reference-locations')) customElements.define('ol-reference-locations', ReferenceLocations);
